import { Flex, useDisclosure } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useRegions from './hooks/useRegions';
import useDeleteTRSCluster from './queries/useDeleteTRSCluster';
import useGetTRSCluster from './queries/useGetTRSCluster';
import useUpdateTRSCluster from './queries/useUpdateTRSCluster';

import FormBase from '@/components/forms/new/FormBase';
import { Button, LoadingIndicator } from 'components/v4';
import {
    breadCrumbTrailEdit as breadCrumbTrail,
    editHeader as header,
    trsPath,
} from 'pages/ga/trs-clusters/constants';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import TRSClusterNameField from './formFields/TRSClusterNameField';
import TRSClusterRegionField from './formFields/TRSClusterRegionField';

const TRSClustersEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);

    const [isDisabled, setIsDisabled] = useState(true);
    const [currentCluster, setCurrentCluster] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Use shared regions hook instead of separate data fetching
    const {
        data: regions = [],
        isLoading: regionsLoading,
        isFetching: regionsFetching,
        isRefetching: regionsRefetching,
        error: regionsError,
    } = useRegions();

    // Fetch single cluster data
    const {
        data: clusterData,
        isLoading: clusterLoading,
        error: clusterError,
        isFetching: clusterFetching,
    } = useGetTRSCluster(id);

    useEffect(() => {
        if (clusterData) {
            setCurrentCluster(clusterData);
        }
    }, [clusterData]);

    const handleUpdateSuccess = () => {
        toast.success('TRS Cluster updated successfully!');
        navigate(trsPath);
    };

    const handleUpdateError = (error) => {
        console.error('Failed to update TRS Cluster:', error);
        toast.error('Failed to update TRS Cluster. Please try again.');
    };

    const handleDeleteSuccess = () => {
        toast.success('TRS Cluster deleted successfully!');
        navigate(trsPath);
        onClose();
    };

    const handleDeleteError = (error) => {
        console.error('Failed to delete TRS Cluster:', error);
        toast.error('Failed to delete TRS Cluster. Please try again.');
    };

    const { mutate: updateCluster, isPending: updatePending } =
        useUpdateTRSCluster(handleUpdateSuccess, handleUpdateError);

    const { mutate: deleteCluster, isPending: deletePending } =
        useDeleteTRSCluster(handleDeleteSuccess, handleDeleteError);

    const finalFormSubmit = async (values) => {
        // Basic validation
        if (!values.name || values.name.length < 2) {
            console.error(
                'Cluster name is required and must be at least 2 characters',
            );
            return;
        }

        if (!values.regionID) {
            console.error('Region is required');
            return;
        }

        const finalSubmit = {
            id: id,
            name: values.name,
            regionID: values.regionID,
        };

        updateCluster(finalSubmit);
    };

    const handleDeleteConfirm = () => {
        deleteCluster(id);
    };

    const formFields = (errors, touched, values) => {
        const fields = [
            <TRSClusterNameField
                key="name"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <TRSClusterRegionField
                key="regionID"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                regions={regions}
                isDisabled={isDisabled}
                mode="Edit"
                clusterData={currentCluster}
            />,
        ];

        if (values?.name && values?.regionID) {
            setIsDisabled(false);
        }

        return fields;
    };

    const extraButton = (
        <Flex justify="flex-end" width="100%">
            <Button
                w={142}
                colorScheme="red"
                onClick={onOpen}
                isDisabled={deletePending || updatePending}>
                Delete
            </Button>
        </Flex>
    );

    const isFetchingData =
        regionsLoading ||
        regionsRefetching ||
        regionsFetching ||
        clusterLoading ||
        clusterFetching ||
        updatePending;

    // Handle error states
    if (clusterError) {
        toast.error(
            'Failed to load cluster data. Redirecting to clusters list.',
        );
        navigate(trsPath);
        return <LoadingIndicator />;
    }

    if (regionsError) {
        toast.error('Failed to load regions. Please refresh the page.');
    }

    // Show loading while data is being fetched
    if (isFetchingData || !currentCluster) {
        return <LoadingIndicator />;
    }

    // Prepare initial values with fetched cluster data
    const editInitialValues = {
        name: currentCluster.name || '',
        regionID: currentCluster.region?.id || '',
    };

    return (
        <>
            <FormBase
                isDisabled={isDisabled}
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={editInitialValues}
                finalFormSubmit={finalFormSubmit}
                backLink={[trsPath, { state: { index: 0 } }]}
                extraButton={extraButton}>
                {({ errors, touched, values }) =>
                    formFields(errors, touched, values)
                }
            </FormBase>

            <DeleteConfirmationModal
                isOpen={isOpen}
                onClose={onClose}
                clusterName={currentCluster?.name}
                onConfirm={handleDeleteConfirm}
                isDeleting={deletePending}
            />
        </>
    );
};

export default TRSClustersEdit;
